import urlParse from 'url-parse';
import {
  Plan,
  CreditPlan,
  FeaturedPlan,
  PlanType,
  URL_PLAN_TYPE,
  PLAN_TYPE,
  SUBSCRIPTION_TYPE,
} from './doppler-types';
import countriesEs from './i18n/countries-es.json';
import countriesEn from './i18n/countries-en.json';

declare global {
  interface Window {
    _LTracker: any;
  }
}

export function timeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const languageOptions = [
  { key: 'es', labelId: 'common.language.spanish' },
  { key: 'en', labelId: 'common.language.english' },
];

export function getDataHubParams(partialUrl: string) {
  const parsedUrl = urlParse(partialUrl, false);
  return {
    navigatedPage: parsedUrl.pathname,
    hash: parsedUrl.hash,
    search: parsedUrl.query,
  };
}

interface ResponseWithEtag {
  headers: { [key: string]: string };
}

export class ResponseCache {
  private _cachedResults: { [key: string]: { etag: string; value: any } } = {};
  public getCachedOrMap<TResponse extends ResponseWithEtag, TResult>(
    func: Function,
    response: TResponse,
    mapFunction: (r: TResponse) => TResult,
  ) {
    if (!response.headers.etag) {
      return mapFunction(response);
    }
    const functionName = func.name;
    if (
      response.headers.etag !==
      (this._cachedResults[functionName] && this._cachedResults[functionName].etag)
    ) {
      this._cachedResults[functionName] = {
        value: mapFunction(response),
        etag: response.headers.etag,
      };
    }
    return this._cachedResults[functionName].value;
  }
}

export function addDays(date: Date, days: number) {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + days);
  return newDate;
}

export function getStartOfDate(date: Date) {
  return typeof date.getMonth === 'function'
    ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
    : undefined;
}

export function addLogEntry(data: any) {
  window._LTracker.push(data);
}

export function logAxiosRetryError(error: any, logger: any, window: any) {
  try {
    const data = JSON.parse(error?.config?.data);
    logger({
      origin: window.location.origin,
      section: 'Axios Retry Try',
      account: data?.Email || data?.Username,
      signupOrigin: data?.Origin,
      postUrl: error?.config?.url,
      browser: window.navigator.userAgent,
      message: error?.message,
    });
  } catch {
    logger({
      origin: window.location.origin,
      section: 'Axios Retry Catch',
      postUrl: error?.config?.url,
      browser: window.navigator.userAgent,
      message: error?.message,
      data: error?.config?.data,
    });
  }
}

export function getSubscriberStatusCssClassName(status: string) {
  let subscriberCssClass = '';
  switch (status) {
    case 'active':
      subscriberCssClass = 'user--active';
      break;
    case 'inactive':
      subscriberCssClass = 'user--active-with-no-list';
      break;
    case 'unsubscribed_by_hard':
      subscriberCssClass = 'user--removed-hard-bounced';
      break;
    case 'unsubscribed_by_soft':
      subscriberCssClass = 'user--removed-soft-bounced';
      break;
    case 'unsubscribed_by_subscriber':
      subscriberCssClass = 'user--removed-subscriber';
      break;
    case 'unsubscribed_by_never_open':
      subscriberCssClass = 'user--removed-no-openings';
      break;
    case 'pending':
      subscriberCssClass = 'user--pending';
      break;
    case 'unsubscribed_by_client':
      subscriberCssClass = 'user--removed-client';
      break;
    case 'standBy':
      subscriberCssClass = 'user--stand-by';
      break;
    default:
      break;
  }
  return subscriberCssClass;
}

export function extractParameter(
  location: any,
  parseQueryStringFunction: any,
  paramLowercase: string,
  paramUppercase?: string,
) {
  const parsedQuery = location && location.search && parseQueryStringFunction(location.search);
  return (
    (parsedQuery &&
      (parsedQuery[paramLowercase] || (!!paramUppercase && parsedQuery[paramUppercase]))) ||
    null
  );
}

export function isWhitelisted(url: string) {
  const loginWhitelist = [
    'https://academy.fromdoppler.com/',
    'http://academy.fromdoppler.com/',
    'http://prod.doppleracademy.com/',
    'https://prod.doppleracademy.com/',
    'https://doppleracademy.com/',
    'http://doppleracademy.com/',
    'https://academyqa.fromdoppler.com/',
    'https://qa.doppleracademy.com',
    'https://app2.bancoprovinciamail.com.ar/',
    'https://app2.fromdoppler.com/',
    'http://localhost:',
    'http://localhost/',
    'https://localhost:',
    'https://localhost/',
    'https://appint.fromdoppler.com/',
    'https://appqa.fromdoppler.com/',
    'https://goemms.com/',
    'http://goemms.com/',
    'https://app.fromdoppler.com/',
    'https://webappqa.fromdoppler.net/',
    'https://webappint.fromdoppler.net/',
    'https://reports2.fromdoppler.com/',
    'https://reportsqa.fromdoppler.net:400/',
    'https://reportsint.fromdoppler.net:400/',
    'https://landings.fromdoppler.com/',
    'https://landingsqa.fromdoppler.net/',
  ];
  return !!url && loginWhitelist.some((element) => url.startsWith(element));
}

export const replaceSpaceWithSigns = (url: string) => {
  return url ? url.replace(' ', '+') : '';
};

const urlsWebApp: any = [
  {
    url: '/reports',
    menu: 'reportMenu',
    subMenu: 'webapp',
  },
  {
    url: '/integrations/shopify',
    menu: '',
    subMenu: '',
  },
  {
    url: '/reports/master-subscriber',
    menu: 'reportMenu',
    subMenu: '',
  },
  {
    url: '/reports/subscriber-history',
    menu: 'listMenu',
    subMenu: '',
  },
  {
    url: '/reports/subscriber-gdpr',
    menu: 'listMenu',
    subMenu: '',
  },
  {
    url: '/reports/partials-campaigns',
    menu: 'reportMenu',
    subMenu: '',
  },
];

export function getCurrentPageForUrl(currentUrl: string): any {
  return urlsWebApp.find((item: any) => item.url === currentUrl);
}

export function getPlanFee(plan: Plan): number {
  return plan.type === 'prepaid' ? (plan as CreditPlan).price : (plan as FeaturedPlan).fee;
}

export const amountByPlanType = (plan: Plan): number => {
  switch (plan.type) {
    case PLAN_TYPE.byCredit:
      return plan.credits || plan.emailQty || 0;

    case PLAN_TYPE.byContact:
      return plan.subscriberLimit || plan.subscribersQty || 0;

    case PLAN_TYPE.byEmail:
      return plan.emailsByMonth || plan.emailQty || 0;
    default:
      return 0;
  }
};

export const firstPlansDefaultOrder: PlanType[] = ['subscribers', 'monthly-deliveries', 'prepaid'];

export function orderPlanTypes(
  planTypes: PlanType[],
  firstPlans: PlanType[] = firstPlansDefaultOrder,
): PlanType[] {
  const firstPlansIncluded: PlanType[] = firstPlans.filter((planType: PlanType) =>
    planTypes.includes(planType),
  );

  if (firstPlansIncluded.length === 0) {
    return planTypes;
  }

  const complementaryPlans = planTypes.filter(
    (plantType: PlanType) => !firstPlansIncluded.includes(plantType),
  );
  return [...firstPlansIncluded, ...complementaryPlans];
}

interface Link {
  rel: string;
  href: string;
  description?: string;
}

var syntaxChars = /[\^$\\.*+?()[\]{}|]/g;

function escapeRegExp(value: string) {
  return value.replace(syntaxChars, '\\$&');
}

export function searchLinkByRel(links: Link[], rel: string): Link[] {
  var regularExpression = new RegExp('\\b' + escapeRegExp(rel) + '\\b');
  return links.filter((l) => regularExpression.test(l.rel));
}

// Due to it seems to be that RAE defines that numbers has to be grouped when has more than 4 digits,
// (so 1000 it shouldn't to be 1.000), it was decided to use German language when Spanish.
export const thousandSeparatorNumber = (lang: string, value: number) =>
  new Intl.NumberFormat(lang === 'es' ? 'de' : lang, {
    minimumFractionDigits: value % 1 ? 2 : 0,
    useGrouping: true,
  }).format(value);

export const unitPriceDecimals = (value: number) => value.toFixed(5);

export const compactNumber = new Intl.NumberFormat('en', {
  //@ts-ignore
  notation: 'compact',
  //@ts-ignore
  compactDisplay: 'short',
}).format;

export const getCountries = (language: string) => {
  const data: { [key: string]: any } = language === 'es' ? countriesEs : countriesEn;
  const countries = Object.keys(data).map(function (key) {
    return { key: key, value: data[key] };
  });

  return countries.sort((a, b) =>
    a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }),
  );
};

export const getFormInitialValues = (fieldNames: object) =>
  Object.keys(fieldNames).reduce(
    (accumulator, currentValue) => ({ ...accumulator, [currentValue]: '' }),
    {},
  );

// Time (in milliseconds ) that the message is displayed on the screen.
export const successMessageDelay = 3000;

export const concatClasses = (...args: any[]) => {
  return args.filter((x) => x).join(' ');
};

export const getPlanTypeFromUrlSegment = (planTypeUrlSegment: string) => {
  switch (planTypeUrlSegment) {
    case URL_PLAN_TYPE[PLAN_TYPE.byContact]:
    case PLAN_TYPE.byContact:
      return PLAN_TYPE.byContact;

    case URL_PLAN_TYPE[PLAN_TYPE.byEmail]:
    case PLAN_TYPE.byEmail:
      return PLAN_TYPE.byEmail;

    case URL_PLAN_TYPE[PLAN_TYPE.byCredit]:
    case PLAN_TYPE.byCredit:
      return PLAN_TYPE.byCredit;

    default:
      return 'unknown';
  }
};

export const objectKeystoLowerCase = (obj: any) =>
  Object.keys(obj).reduce((acc: any, key) => {
    acc[key.toLowerCase()] = obj[key];
    return acc;
  }, {});

export const ACCOUNT_TYPE = 'accountType';
export const PAID_ACCOUNT = 'PAID';
export const FREE_ACCOUNT = 'FREE';

const getAccountType = (isFreeAccount: any) => (isFreeAccount ? FREE_ACCOUNT : PAID_ACCOUNT);

export const getQueryParamsWithAccountType = ({
  search,
  isFreeAccount,
}: {
  search: string;
  isFreeAccount: boolean;
}) => {
  const params = new URLSearchParams(search);
  const currentAccountType = getAccountType(isFreeAccount);

  if (!params.has(ACCOUNT_TYPE)) {
    params.append(ACCOUNT_TYPE, currentAccountType);
  } else if (params.get(ACCOUNT_TYPE) !== currentAccountType) {
    params.set(ACCOUNT_TYPE, currentAccountType);
  }
  return params.toString();
};

export const orderPaymentFrequencies = (currentDiscount: any, nextDiscount: any) =>
  nextDiscount.numberMonths - currentDiscount.numberMonths;

export const getMonthsByCycle = (subscriptionType: string) => {
  switch (subscriptionType) {
    case SUBSCRIPTION_TYPE.monthly:
      return 1;
    case SUBSCRIPTION_TYPE.quarterly:
      return 3;
    case SUBSCRIPTION_TYPE.biyearly:
      return 6;
    case SUBSCRIPTION_TYPE.yearly:
      return 12;
    default:
      return 1;
  }
};

export const getInfoPlanType = (planType: PlanType): string | null => {
  switch (planType) {
    case PLAN_TYPE.byContact:
      return 'buy_process.plan_selection.plan_type_subscribers_info';

    case PLAN_TYPE.byEmail:
      return 'buy_process.plan_selection.plan_type_monthly_deliveries_info';

    case PLAN_TYPE.byCredit:
      return 'buy_process.plan_selection.plan_type_prepaid_info';
    default:
      return null;
  }
};
